import { useAuth } from './authHooks';
import { useAuth0 } from '@auth0/auth0-react';
import { purgeVocabWordStateLocalState } from '../insights/cheatsheet/vocab/vocabProgressStore';
import {
  useCanonicalProductPath,
  useProductDeploymentProfile,
} from '../navigation/ProductNavigation';

/**
 * The single, Auth0-aware sign-out used by every "Sign out" button.
 *
 * Why this exists: a local-only logout (just clearing our token) is NOT enough for a
 * Google/Auth0 session. Auth0 keeps its own SSO session, so after a local-only logout
 * the very next visit to /login silently re-authenticates the user (LoginPage's Auth0
 * sync effect: isAuthenticated && !user -> getAccessTokenSilently -> handleExternalLogin)
 * and the gate sends an inactive user straight back to /access-pending — the login form
 * never appears. So for an Auth0 session we MUST also clear the Auth0 session via
 * auth0Logout (returnTo must be in Auth0's Allowed Logout URLs). For a local/password
 * session we just clear our token and return to /login.
 */
export function useSignOut(options: { returnToPath?: string } = {}): () => void {
  const { logout: localLogout } = useAuth() as { logout: () => void };
  const { logout: auth0Logout, isAuthenticated: isAuth0Session } = useAuth0();
  const canonicalProductPath = useCanonicalProductPath();
  const deploymentProfile = useProductDeploymentProfile();

  return () => {
    const returnToPath = options.returnToPath
      ?? (deploymentProfile === 'www-candidate' ? '/login' : '');
    const canonicalReturnToPath = returnToPath ? canonicalProductPath(returnToPath) : '';
    const auth0ReturnTo = `${window.location.origin}${canonicalReturnToPath}`;
    const localReturnTo = canonicalProductPath(returnToPath || '/login');

    localLogout(); // clear our token + user immediately
    purgeVocabWordStateLocalState();
    if (isAuth0Session) {
      auth0Logout({ logoutParams: { returnTo: auth0ReturnTo } });
    } else {
      // Full navigation (not client-side) guarantees a clean slate on the login form.
      window.location.assign(localReturnTo);
    }
  };
}
